import { CloseOutlined, UpOutlined } from '@ant-design/icons';
import { BackTop, Button, Col, Layout, Row } from 'antd';
import { config } from 'config/config';
import routePaths, {
  isAccountPage,
  isExperiences,
  isHome,
  isScanPage,
  isStaticPage,
} from 'config/routes';
import { useRouter } from 'next/router';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import CustomBreadcrumb from 'src/components/Breadcrumb';
import HeaderBanner from 'src/components/layout/banner/HeaderBanner';
import BasicFooter from 'src/components/layout/BasicFooter';
import BasicHeader from 'src/components/layout/BasicHeader';
import ValidateNewsleterProfiling from 'src/components/layout/ValidateNewsletterProfiling';
import Link from 'src/components/Link';
import { IRootReducers } from 'src/reducers';
import { PlatformEnum, UserAccountStatusEnum } from 'src/shared/enums';
import { isConPlatform, useWindowSize } from 'utils';
import Consumables from '../consumables/consumables';
import TermsConditionsModal from '../termsConditions/termsConditionsModal';
import DiscoveryCouponsModal from '../consumables/discoveryCouponsModal';
import { ICampaign } from 'src/shared/models';
import ConsumableSpecialDiscoveryCoupon from '../consumables/consumableSpecialCoupon';
import { AnyAction, Dispatch, bindActionCreators } from 'redux';
import {
  setShowDiscoveryModal,
  setShowSpecialDiscoveryCoupon,
  setUserCampaigns,
} from 'src/actions/appActions';
import DiscoveryDesktopBanner from '../consumables/discoveryDesktopBanner';
import * as api from 'services/api';
import { openToast } from '../general/Toast';
import getConfig from 'next/config';
import { DiscoveryCampaignsConfig } from 'config/discovery-campaigns';

const { DEFAULT_TIMEZONE } = getConfig().publicRuntimeConfig;

const {
  CON_ARGENTINA,
  CON_BRAZIL,
  CON_CHILE,
  CON_COLOMBIA,
  CON_DEUTSCHLAND,
  CON_FRANCE,
  CON_ITALY,
  CON_MEXICO,
  CON_PERU,
  CON_PORTUGAL,
  CON_SPAIN,
} = PlatformEnum;

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

const BasicLayout: FC<Props> = ({
  authenticated,
  user,
  children,
  breadCrumbs,
  staticPageConfig,
  platformStatus,
  profiling,
  scanData,
  userCampaigns,
  showDiscoveryModal,
  setUserCampaigns,
  setShowSpecialDiscoveryCoupon,
}) => {
  const { route, pathname } = useRouter();
  const router = useRouter();
  const { query } = router;
  const [isInLogin, setIsInLogin] = useState(false);
  const [isInMyAccount, setIsInMyAccount] = useState(false);
  const [showStickyHeader, setShowStickyHeader] = useState(true);
  const [profilingAccepted, setProfilingAccepted] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const isPublicHome: boolean = isHome(pathname) && !user;
  const isPageExperiences: boolean = isExperiences(pathname);
  const isPageMyAccount: boolean = isAccountPage(pathname);
  const isPageScan: boolean = isScanPage(pathname);
  const isPageToShowModal: boolean = !isStaticPage(pathname);
  const isPrivateHome: boolean = isHome(pathname) && user ? true : false;
  const [showSpecialCoupon, setShowSpecialCoupon] = useState(false);
  const [campaignId, setCampaignId] = useState(null);
  const [discoveryCoupon, setDiscoveryCoupon] = useState([]);
  const isCampaign: boolean = query.campaign && user ? true : false;

  const pregnancyLanding =
    pathname === routePaths.PAGES.LOGIN && query.pregnancy === 'true';

  const changeAutumnRevamp = (): boolean => {
    const country = selectCountry();
    const setCountries = new Set([
      'España',
      'Deutschland',
      'Italy',
      'Portugal',
      'México',
    ]);
    if (setCountries.has(country)) return true;
    else return false;
  };

  const changeSpringRevamp = (): boolean => {
    const country = selectCountry();
    const setCountries = new Set([
      'España',
      'Portugal',
      'Deutschland',
      'Brazil',
    ]);
    if (setCountries.has(country)) return true;
    else return false;
  };

  const { isMD } = useWindowSize();
  const { formatMessage } = useIntl();

  const checkCampaigns = (campaign: ICampaign): boolean => {
    if (!campaign) return false;
    else {
      const timezone = parseInt(DEFAULT_TIMEZONE) * 60 * 60 * 10;
      const checkExpirationDate: boolean =
        new Date(campaign.expirationDate).getTime() - timezone <
        new Date().getTime();
      if (checkExpirationDate) return false;
    }
    return true;
  };

  const selectCountry = (): string => {
    const countryPlatform = config.APP.PLATFORM;
    switch (countryPlatform) {
      case CON_ARGENTINA:
        return 'Argentina';
      case CON_CHILE:
        return 'Chile';
      case CON_COLOMBIA:
        return 'Colombia';
      case CON_MEXICO:
        return 'México';
      case CON_PERU:
        return 'Peru';
      case CON_BRAZIL:
        return 'Brazil';
      case CON_DEUTSCHLAND:
        return 'Deutschland';
      case CON_ITALY:
        return 'Italy';
      case CON_PORTUGAL:
        return 'Portugal';
      case CON_FRANCE:
        return 'France';
      default:
        return 'España';
    }
  };

  const hideFooter = (route: string) => {
    switch (route) {
      case routePaths.PAGES.MY_ACCOUNT.ACCOUNT_INDEX:
      case routePaths.PAGES.MY_ACCOUNT.HISTORY:
      case routePaths.PAGES.MY_ACCOUNT.MY_POINTS:
      case routePaths.PAGES.MY_ACCOUNT.MY_BONUS:
      case routePaths.PAGES.MY_ACCOUNT.ORDERS:
      case routePaths.PAGES.MY_ACCOUNT.MY_PROFILE:
      case routePaths.PAGES.MY_ACCOUNT_EDIT:
      case routePaths.PAGES.MY_ACCOUNT.MY_ADDRESSES:
      case routePaths.PAGES.MY_CENTER.MY_INVOICES:
      case routePaths.PAGES.MY_CENTER.CENTER_PROFILE:
      case routePaths.PAGES.MY_CENTER.CENTER_MEMBERS:
        return true;
      default:
        return staticPageConfig?.hideFooter;
    }
  };

  const hideModalNewsletters = (route: string) => {
    switch (route) {
      case routePaths.STATIC:
        return true;
      default:
        return false;
    }
  };

  const isCampaignDiscoverySpecial =
    userCampaigns &&
    userCampaigns?.type &&
    !!DiscoveryCampaignsConfig?.[userCampaigns.type];
  let campaignSpecial;
  if (isCampaignDiscoverySpecial) {
    campaignSpecial = DiscoveryCampaignsConfig?.[userCampaigns?.type];
  }

  const discoveryCouponLayoutBackgroundStylesCampaign = (): {
    background: string;
  } => {
    return isCampaignDiscoverySpecial
      ? { background: campaignSpecial?.styles?.banner?.myCoupons?.background }
      : undefined;
  };

  useEffect(() => {
    if (route === routePaths.PAGES.LOGIN) {
      setIsInLogin(true);
    } else {
      setIsInLogin(false);
    }
    if (user && isHome(pathname) && !userCampaigns) {
      setUserCampaigns();
    }
  }, [route]);

  useEffect(() => {
    if (isPrivateHome && checkCampaigns(userCampaigns)) {
      setShowSpecialDiscoveryCoupon(true);
      setShowSpecialCoupon(true);
    } else {
      setShowSpecialDiscoveryCoupon(false);
      setShowSpecialCoupon(false);
    }
  }, [route, userCampaigns]);

  useEffect(() => {
    if (
      route === routePaths.PAGES.MY_ACCOUNT.MY_ADDRESSES ||
      route === routePaths.PAGES.MY_ACCOUNT.MY_POINTS ||
      route === routePaths.PAGES.MY_ACCOUNT.MY_BONUS ||
      route === routePaths.PAGES.MY_ACCOUNT.MY_PROFILE
    ) {
      setIsInMyAccount(true);
    } else {
      setIsInMyAccount(false);
    }
  }, [route]);

  useEffect(() => {
    if (profiling === 0 && user.userStatus === UserAccountStatusEnum.GRANT) {
      setProfilingAccepted(false);
    }
  }, [user]);

  useEffect(() => {
    setShowModal(
      ((scanData?.fromScan && !!scanData.newCoupon) ||
        (scanData?.welcome && scanData?.userConsumableCoupon?.length > 0)) &&
        user?.termsAccepted &&
        !(scanData?.fromLogin === false && scanData?.fromLoggedDiScan === true),
    );
  }, [scanData, user]);

  const openNotification = () => {
    const toastMessage = formatMessage({
      id: 'consumables.discovery.message.error',
    });
    openToast(toastMessage, 'error', 4, 'cross-red');
  };

  const isCampaignActiveForTheUser = async (campaignId) => {
    try {
      const previewDiscoveryCouponResponse = await api.previewDiscoveryCampaign(
        {
          campaignId: campaignId,
        },
      );

      if (previewDiscoveryCouponResponse?.data?.newCoupons) {
        setDiscoveryCoupon(previewDiscoveryCouponResponse.data.newCoupons);
        if (query.campaign) {
          setCampaignId(query.campaign);
        } else if (userCampaigns?.id !== null) {
          setCampaignId(userCampaigns.id);
        }
      } else {
        openNotification();
      }
    } catch (err) {
      openNotification();
      return;
    }
  };

  useEffect(() => {
    if (isCampaign) isCampaignActiveForTheUser(query.campaign);
  }, [route, isCampaign]);

  const addPageClass = (page: string): string => {
    const path = page.replace('/', '');
    if (!path) {
      return 'main';
    }

    if (path.includes('/')) {
      const slashIndex = path.indexOf('/');
      return path.substring(0, slashIndex);
    }

    return page.replace('/', '');
  };

  useEffect(() => {
    if (showDiscoveryModal && userCampaigns?.id) {
      isCampaignActiveForTheUser(userCampaigns.id);
    }
  }, [showDiscoveryModal]);

  const hideBasicFooter =
    platformStatus === false || (hideFooter(route) && !isConPlatform);

  const componentLogin = isConPlatform &&
    !authenticated &&
    showStickyHeader &&
    isPublicHome && (
      <Row
        className={
          changeAutumnRevamp()
            ? 'basicHeader__sticky-login-autumn'
            : changeSpringRevamp()
            ? 'basicHeader__sticky-login-spring'
            : 'basicHeader__sticky-login-winter'
        }>
        <Col span={1} />
        <Col span={22}>
          <Row className="basicHeader__cta">
            <Link href={routePaths.PAGES.LOGIN}>
              <span
                className={
                  (changeAutumnRevamp()
                    ? 'basicHeader__btn-ghost-login-autumn'
                    : changeSpringRevamp()
                    ? 'basicHeader__btn-ghost-login-spring'
                    : 'basicHeader__btn-ghost-login-winter') + ' action-label'
                }>
                <FormattedMessage id="menu.header.login" />
              </span>
            </Link>
            <span
              className={
                changeAutumnRevamp()
                  ? 'separatorAutumn'
                  : changeSpringRevamp()
                  ? 'separatorSpring'
                  : 'separatorWinter'
              }>
              <FormattedMessage id="menu.header.or" />
            </span>
            <Link href={routePaths.PAGES.LOGIN}>
              <span
                className={
                  (changeAutumnRevamp()
                    ? 'basicHeader__btn-ghost-login-autumn'
                    : changeSpringRevamp()
                    ? 'basicHeader__btn-ghost-login-spring'
                    : 'basicHeader__btn-ghost-login-winter') + ' action-label'
                }>
                <FormattedMessage id="menu.header.register" />
              </span>
            </Link>
          </Row>
        </Col>
        <Col span={1}>
          <Button
            className={
              'btn-ghost btn-ghost--minimalist ' +
              (changeSpringRevamp()
                ? 'basicHeader__btn-ghost-close-spring'
                : 'basicHeader__btn-ghost-close-winter')
            }
            style={{ display: 'flex' }}
            icon={<CloseOutlined />}
            onClick={() => {
              setShowStickyHeader(false);
            }}
          />
        </Col>
      </Row>
    );

  const showHeader = () => {
    if (!user) return true;
    else if (!isPageToShowModal) {
      if (user?.termsAccepted) return true;
      else return false;
    } else if (pregnancyLanding) return false;
    return true;
  };

  const basicLayoutStyles = () => {
    if (user && pregnancyLanding) return 'pregnancy-layout';
    else return 'basic-layout';
  };

  return (
    <Layout className={`${basicLayoutStyles()} ${addPageClass(route)} `}>
      {showHeader() && <BasicHeader />}
      {showHeader() && <HeaderBanner />}

      {showSpecialCoupon &&
        (isMD ? (
          <div
            className="discoveryCouponLayoutContainer"
            style={discoveryCouponLayoutBackgroundStylesCampaign()}>
            <ConsumableSpecialDiscoveryCoupon
              campaign={userCampaigns}
              discoveryCoupons={discoveryCoupon}
              fromLayout={true}
            />
          </div>
        ) : (
          <DiscoveryDesktopBanner
            campaign={userCampaigns}
            discoveryCoupons={discoveryCoupon}
            setDiscoveryCoupons={setDiscoveryCoupon}
          />
        ))}
      <div className="sticky-modal">{componentLogin}</div>
      <Layout.Content
        className={`layout__content
              ${isInLogin && 'main-login'}
              ${isInMyAccount && 'main-my-account'}`}>
        <CustomBreadcrumb breadCrumbs={breadCrumbs} />
        {children}
        <BackTop>
          <UpOutlined className="button-backTop" />
        </BackTop>
      </Layout.Content>
      {isPrivateHome && (
        <ValidateNewsleterProfiling
          user={user}
          profilingAccepted={profilingAccepted}
          setProfilingAccepted={setProfilingAccepted}
          hideModal={hideModalNewsletters(route)}
        />
      )}
      {(isPrivateHome ||
        isPageExperiences ||
        isPageMyAccount ||
        isPageScan) && (
        <Consumables showModal={showModal} setShowModal={setShowModal} />
      )}

      <DiscoveryCouponsModal
        discoveryCoupons={discoveryCoupon}
        campaignId={campaignId}
      />

      <BasicFooter hideFooter={hideBasicFooter} />
      {config.APP.PLATFORM === CON_SPAIN && isPageToShowModal && (
        <TermsConditionsModal />
      )}
    </Layout>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
    authenticated:
      state.auth.accessToken !== null && state.auth.accessToken !== '',
    impersonate: state.auth.impersonate,
    userStatus: state.auth.userStatus,
    breadCrumbs: state.app.breadCrumbs,
    staticPageConfig: state.app.staticPageConfig,
    platformStatus: state.app.platformStatus,
    profiling:
      state.auth.user !== null ? state.auth.user.mcSegmentedSubscription : 1,
    scanData: state.app.scanData,
    userCampaigns: state.app.userCampaigns,
    showSpecialDiscoveryCoupon: state.app.showSpecialDiscoveryCoupon,
    accessToken: state.auth.accessToken,
    showStaticPageDiscoveryModal: state.app.showStaticPageDiscoveryModal,
    showDiscoveryModal: state.app.showDiscoveryModal,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    { setUserCampaigns, setShowSpecialDiscoveryCoupon, setShowDiscoveryModal },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(BasicLayout);
